<template>
  <div class="card shadow-sm" style="max-width: 25rem; min-width: 18rem">
    <div class="card-header mx-0 px-3 border-bottom d-flex">
      <h4 class="ms-2 mb-0 flex-grow-1">Categories</h4>
      <IconButton
        icon-name="material-symbols:add"
        class="text-success"
        @keypress.stop
        @click.stop="openCreateForm"
      />
    </div>
    <div :class="loading === false ? 'overflow-auto custom-scrollbar' : ''">
      <ListGroup
        :loading="loading"
        :default-active-id="defaultActiveId"
        :ids="skus"
        @update-active-list-id="
          (id: string | number | undefined) => $emit('updateActiveListId', `${id}`)
        "
      >
        <ListGroupItem
          v-for="category in categories"
          :key="category.sku"
          :list-id="category.sku"
          :name="category.name"
          :show-collapse-options="getSubCategoriesById(category.id).length > 0 ? true : false"
          :open="openCategories.includes(category.id)"
          @update-open-list="(open: boolean) => addOrRemoveCategoryToOpenList(open, category.id)"
        >
          <template #collapseOptions="{isOpen}">
            <IconButton
              v-if="getSubCategoriesById(category.id).length > 0"
              :icon-name="
                isOpen === true
                  ? 'material-symbols:keyboard-arrow-down'
                  : 'material-symbols:chevron-right'
              "
              size="md"
              @keypress.stop
              @click.stop="toggleOpen(category.id, 'Category')"
            />
            <IconButton v-else icon-name="material-symbols:remove" :hoverable="false" />
          </template>
          <template #actions>
            <CategoryIconButtonGroup
              :show-delete="getSubCategoriesById(category.id).length === 0"
              @update="openUpdateForm(category.id, 'Category')"
              @delete="openDeleteDialog(category.id, 'Category')"
            />
          </template>
          <ListGroupItem
            v-for="subCategory in getSubCategoriesById(category.id)"
            :key="subCategory.sku"
            :list-id="`${category.sku}-${subCategory.sku}`"
            :name="subCategory.name"
            :show-collapse-options="getGroupsById(subCategory.id).length > 0 ? true : false"
            :open="openSubCategories.includes(subCategory.id)"
            @update-open-list="
              (open: boolean) => addOrRemoveSubCategoryToOpenList(open, subCategory.id)
            "
          >
            <template #collapseOptions="{isOpen}">
              <IconButton
                v-if="getGroupsById(subCategory.id).length > 0"
                :icon-name="
                  isOpen === true
                    ? 'material-symbols:keyboard-arrow-down'
                    : 'material-symbols:chevron-right'
                "
                size="md"
                @keypress.stop
                @click.stop="toggleOpen(subCategory.id, 'Sub-Category')"
              />
              <IconButton v-else icon-name="material-symbols:remove" :hoverable="false" />
            </template>
            <template #actions>
              <CategoryIconButtonGroup
                :show-delete="getGroupsById(subCategory.id).length === 0"
                @update="openUpdateForm(subCategory.id, 'Sub-Category')"
                @delete="openDeleteDialog(subCategory.id, 'Sub-Category')"
              />
            </template>
            <ListGroupItem
              v-for="group in getGroupsById(subCategory.id)"
              :key="group.sku"
              :list-id="`${category.sku}-${subCategory.sku}-${group.sku}`"
              :name="group.name"
            >
              <template #collapseOptions>
                <IconButton icon-name="material-symbols:remove" :hoverable="false" />
              </template>
              <template #actions>
                <CategoryIconButtonGroup
                  :show-delete="getProductsById(group.id).length === 0"
                  @update="openUpdateForm(group.id, 'Group')"
                  @delete="openDeleteDialog(group.id, 'Group')"
                />
              </template>
            </ListGroupItem>
          </ListGroupItem>
        </ListGroupItem>
      </ListGroup>
    </div>
    <CategoryDialogController
      :open="isDialogOpen"
      :is-update-form="isUpdateForm"
      :cid="idToUpdate"
      :type="typeToUpdate"
      @close="isDialogOpen = false"
      @submit="submitCategoryForm"
    />
    <DeleteDialog
      :open="isDeleteDialogOpen"
      :type="typeToDelete"
      :loading="categoryLoading"
      @delete="() => deleteCategory()"
      @close="() => (isDeleteDialogOpen = false)"
    />
  </div>
</template>
<script lang="ts" setup>
import {
  ProductCategory,
  ProductSubCategory,
  ProductGroup,
  Product,
} from '@/stores/userManagement/userManagement';

const emits = defineEmits<{
  (event: 'updateActiveListId', id: string): void;
  (event: 'updateOpenLists', cats: number[], subCats: number[]): void;
}>();

interface Props {
  categories: ProductCategory[];
  subCategories: ProductSubCategory[];
  groups: ProductGroup[];
  products: Product[];
  loading?: boolean;
  isProductOffering: boolean | null;
  openCategories: number[];
  openSubCategories: number[];
  selectedSku?: string;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  isProductOffering: false,
  selectedSku: '',
});

// Stores
const productCategoriesStore = useProductCategoriesStore();
const productSubCategoriesStore = useProductSubCategoriesStore();
const productGroupsStore = useProductGroupsStore();

const openCategories = ref<number[]>(props.openCategories || []);
const openSubCategories = ref<number[]>(props.openSubCategories || []);

// Store methods
const {deleteProductCategory} = productCategoriesStore;
const {deleteProductSubCategory} = productSubCategoriesStore;
const {deleteProductGroup} = productGroupsStore;

const categoryLoading = ref(false);

const addOrRemoveCategoryToOpenList = (isOpen: boolean, id: number) => {
  if (isOpen) {
    if (!openCategories.value.includes(id)) {
      openCategories.value.push(id);
    }
  } else {
    openCategories.value = openCategories.value.filter((c) => c !== id);
  }
};

const addOrRemoveSubCategoryToOpenList = (isOpen: boolean, id: number) => {
  if (isOpen) {
    if (!openSubCategories.value.includes(id)) {
      openSubCategories.value.push(id);
    }
  } else {
    openSubCategories.value = openSubCategories.value.filter((c) => c !== id);
  }
};

const toggleOpen = (id: number, type: string) => {
  if (type === 'Category') {
    addOrRemoveCategoryToOpenList(!openCategories.value.includes(id), id);
  } else if (type === 'Sub-Category') {
    addOrRemoveSubCategoryToOpenList(!openSubCategories.value.includes(id), id);
  }
  emits('updateOpenLists', openCategories.value, openSubCategories.value);
};

const defaultActiveId = computed(() => {
  if (props.isProductOffering) {
    return '';
  }
  return props.categories.length > 0 ? props.selectedSku : '';
});

const getSubCategoriesById = (id: number) => {
  return props.subCategories.length > 0
    ? props.subCategories.filter(({category_id}) => category_id === id)
    : [];
};
const getGroupsById = (id: number) => {
  return props.groups.length > 0
    ? props.groups.filter(({subcategory_id}) => subcategory_id === id)
    : [];
};
const getProductsById = (id: number) => {
  return props.products.length > 0
    ? props.products.filter(({product_group_id}) => product_group_id === id)
    : [];
};

// Post & Put
const isDialogOpen = ref(false);
const idToUpdate = ref();
const typeToUpdate = ref();
const isUpdateForm = computed(() => {
  return idToUpdate.value !== undefined ? true : false;
});
const openCreateForm = () => {
  idToUpdate.value = undefined;
  typeToUpdate.value = undefined;
  isDialogOpen.value = true;
};
const openUpdateForm = (id: number, type: string) => {
  idToUpdate.value = id;
  typeToUpdate.value = type;
  isDialogOpen.value = true;
};
const submitCategoryForm = () => {
  isDialogOpen.value = false;
  idToUpdate.value = undefined;
};

// Delete
const isDeleteDialogOpen = ref(false);
const idToDelete = ref(0);
const typeToDelete = ref();
const openDeleteDialog = (id: number, type: string) => {
  isDeleteDialogOpen.value = true;
  idToDelete.value = id;
  typeToDelete.value = type;
};

const deleteCategory = async () => {
  try {
    categoryLoading.value = true;
    switch (typeToDelete.value) {
      case 'Category':
        await deleteProductCategory(idToDelete.value);
        break;
      case 'Sub-Category':
        await deleteProductSubCategory(idToDelete.value);
        break;
      case 'Group':
        await deleteProductGroup(idToDelete.value);
        break;
    }
  } catch (errorMessage) {
    console.log(errorMessage);
  } finally {
    categoryLoading.value = false;
    isDeleteDialogOpen.value = false;
  }
};

/**
 * Custom sku id structure for category-subCategory-group (eg. MW-MWEP-MWEPT1)
 * used to keep track of and compare active categories in @see ListGroup.vue
 */
const skus = computed(() => {
  const groups = props.groups.map((g) => {
    const sc = props.subCategories.find((sc) => sc.id === g.subcategory_id);
    const c = props.categories.find((c) => c.id === sc?.category_id);
    return `${c?.sku}-${sc?.sku}-${g?.sku}`;
  });
  const subCategories = props.subCategories.map((sc) => {
    const c = props.categories.find((c) => c.id === sc?.category_id);
    return `${c?.sku}-${sc?.sku}`;
  });
  const categories = props.categories.map((c) => c.sku);
  return [...categories, ...subCategories, ...groups];
});
</script>
<style lang="scss" scoped>
.card {
  --bs-card-cap-padding-y: 1rem;
  --bs-card-border-width: 0;
}

:deep(.list-group-item) > .btn-group {
  transition: all 0.2s ease-out;
  opacity: 0;
}

:deep(.list-group-item:hover) > .btn-group {
  opacity: 1;
}
</style>
